/**
 * Agent Action Inbox Page
 *
 * Full inbox view for managing AI agent suggested actions.
 * Shows pending actions with filters, table, and tabs for history.
 *
 * Date: 2026-02-18
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, RefreshCw, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ActionDetailDialog } from '@/components/inbox/action-detail-dialog';
import {
  getPendingActions,
  getActionHistory,
  getAction,
  getAgentNames,
  getPriorityColor,
  type AgentAction,
} from '@/services/agentActionService';
import { useSite } from '@/contexts/SiteContext';

const InboxPage = () => {
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();

  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [pendingActions, setPendingActions] = useState<AgentAction[]>([]);
  const [historyActions, setHistoryActions] = useState<AgentAction[]>([]);
  const [agents, setAgents] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filters
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterAgent, setFilterAgent] = useState<string>('all');

  // Dialog state
  const [selectedAction, setSelectedAction] = useState<AgentAction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    document.title = 'Action Inbox | PageMind Admin';
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const options: Record<string, string | undefined> = {};
      if (filterPriority !== 'all') options.priority = filterPriority;
      if (filterAgent !== 'all') options.agentName = filterAgent;
      if (selectedSite?.id) options.siteId = selectedSite.id;

      const [pendingRes, historyRes, agentsRes] = await Promise.all([
        getPendingActions(options),
        getActionHistory(options),
        getAgentNames(),
      ]);

      setPendingActions(pendingRes.data);
      setHistoryActions(historyRes.data);
      setAgents(agentsRes);
    } catch (error) {
      console.error('Failed to fetch inbox data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filterPriority, filterAgent, selectedSite?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle action param from URL (e.g., from bell dropdown)
  useEffect(() => {
    const actionId = searchParams.get('action');
    if (actionId) {
      const loadAction = async () => {
        try {
          const result = await getAction(actionId);
          setSelectedAction(result.data);
          setDialogOpen(true);
        } catch (error) {
          console.error('Failed to load action:', error);
        }
      };
      loadAction();
    }
  }, [searchParams]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const handleRowClick = (action: AgentAction) => {
    setSelectedAction(action);
    setDialogOpen(true);
  };

  const handleActionComplete = () => {
    fetchData();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'executed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'dismissed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      case 'expired':
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
    }
  };

  const renderTable = (actions: AgentAction[], showStatus = false) => (
    <div className="bg-card rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Priority</TableHead>
            <TableHead className="w-[140px]">Agent</TableHead>
            <TableHead>Action</TableHead>
            {showStatus && <TableHead className="w-[100px]">Status</TableHead>}
            <TableHead className="w-[120px]">Site</TableHead>
            <TableHead className="w-[140px]">Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                {showStatus && <TableCell><Skeleton className="h-6 w-20" /></TableCell>}
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
              </TableRow>
            ))
          ) : actions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showStatus ? 6 : 5} className="h-24 text-center">
                {activeTab === 'pending'
                  ? 'No pending actions'
                  : 'No action history found'}
              </TableCell>
            </TableRow>
          ) : (
            actions.map((action) => (
              <TableRow
                key={action.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(action)}
              >
                <TableCell>
                  <Badge className={`text-white ${getPriorityColor(action.priority)}`}>
                    {action.priority.charAt(0).toUpperCase() + action.priority.slice(1)}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium">{action.agent_name}</TableCell>
                <TableCell>
                  <div className="truncate max-w-[400px]">{action.title}</div>
                </TableCell>
                {showStatus && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(action.status)}
                      <span className="capitalize text-sm">{action.status}</span>
                    </div>
                  </TableCell>
                )}
                <TableCell className="text-muted-foreground text-sm">
                  {action.site_name || '-'}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDate(action.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Inbox className="mr-2 h-6 w-6 text-primary" />
            Action Inbox
          </h1>
          <p className="text-muted-foreground">
            AI agent suggestions requiring your review and action
          </p>
        </div>

        <div className="flex gap-3 mt-4 md:mt-0">
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterAgent} onValueChange={setFilterAgent}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Agent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent} value={agent}>
                  {agent}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'history')}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            Pending
            {pendingActions.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {pendingActions.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {renderTable(pendingActions, false)}
        </TabsContent>

        <TabsContent value="history">
          {renderTable(historyActions, true)}
        </TabsContent>
      </Tabs>

      {/* Action Detail Dialog */}
      <ActionDetailDialog
        action={selectedAction}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onActionComplete={handleActionComplete}
      />
    </div>
  );
};

export default InboxPage;
